import { Row, TRtkErrorType, useTranslation, Button } from '@packages/utils';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useState, useEffect } from 'react';
import PageWrapper from 'components/Base/PageWrapper';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { useGetElcsQuery } from 'redux/apiSlice/elcs.slice';
import { useGetELCLightBoxQuery } from 'redux/apiSlice/lightbox.slice';
import CardContent from 'components/Base/Layout/Content/CardContent';
import { EIconNames } from 'components/Base/Icon';
import { TabsProps } from 'components/Base/Tabs/Tabs.types';
import useKeepTabsInUrl from 'hooks/useKeepTabsInUrl';
import { ActionPlan as ActionPlanIcon } from 'assets/icon/icon';
import { RoutesPaths } from 'routes/Routes.types';
import { useIsFeatureEnabled } from 'hooks/useIsFeatureEnabled';
import ELCLightboxModal from 'components/Shared/ELCLightboxModal';
import { AllELCsTabs } from './AllELCs.styles';
import ELCItem from '../ELCItem';
import Meetings from '../MeetingsTab';
import Resources from './Resources';

const StyledButton = styled(Button)`
  && svg {
    transition: fill 0.2s ease, stroke 0.2s ease;
  }

  &&:hover svg path {
    fill: #3ba1b8;
    stroke: #3ba1b8;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const AllELCs = () => {
  const navigate = useNavigate();
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.elcs.list.all-elcs',
  });
  const { data, isLoading, error } = useGetElcsQuery();
  const { data: elcLightboxData } = useGetELCLightBoxQuery();
  const [showLightbox, setShowLightbox] = useState(false);

  const isEmpty = !data?.length;

  const isEditAllowed = useCheckPermission('ELC_LIST_UPDATE');
  const hasPillarsViewAccess = useCheckPermission('PILLARS_VIEW');
  const isPillarsManagementEnabled = useIsFeatureEnabled('pillars_management');

  useEffect(() => {
    if (
      elcLightboxData &&
      elcLightboxData.published &&
      !sessionStorage.getItem('elcLightboxSeen')
    ) {
      setShowLightbox(true);
    }
  }, [elcLightboxData]);

  const handleCloseLightbox = () => {
    sessionStorage.setItem('elcLightboxSeen', 'true');
    setShowLightbox(false);
  };

  const ELC_LIST_TABS = {
    ALL_ELCS: 'all-elcs',
    MEETINGS: 'meetings',
    RESOURCES: 'resources',
  } as const;

  const { tabInUrl, onTabChange } = useKeepTabsInUrl({
    defaultTab: ELC_LIST_TABS.ALL_ELCS,
  });

  const tabItems: TabsProps['items'] = [
    {
      key: ELC_LIST_TABS.ALL_ELCS,
      label: (
        <span data-cy="all_elcs_tab">
          {t('list.tabs.ALL_ELCS', 'All ELCs')}
        </span>
      ),
      children: (
        <CardContent
          contentPadding="16px"
          tabContent
          style={{ overflow: 'visible', maxHeight: 'unset' }}
        >
          <Row gutter={[16, 16]} data-cy="elc_list">
            {data?.map(item => (
              <ELCItem
                key={item.elcId}
                elcData={item}
                isEditAllowed={isEditAllowed}
                backButtonSuffix={t('TITLE')}
              />
            ))}
          </Row>
        </CardContent>
      ),
    },
    {
      key: ELC_LIST_TABS.MEETINGS,
      label: (
        <span data-cy="meetings_tab">
          {t('list.tabs.MEETINGS', 'Meetings')}
        </span>
      ),
      children: <Meetings />,
    },
    {
      key: ELC_LIST_TABS.RESOURCES,
      label: (
        <span data-cy="resources_tab">
          {t('list.tabs.RESOURCES', 'Resources')}
        </span>
      ),
      children: <Resources />,
    },
  ];

  const handleActionPlanClick = () => {
    navigate(`/app/${RoutesPaths.ALL_ELCS_ACTION_PLAN}`);
  };

  const actionPlanButton =
    isPillarsManagementEnabled && hasPillarsViewAccess ? (
      <StyledButton
        key="action-plan-button"
        onClick={handleActionPlanClick}
        color="primary"
        icon={<ActionPlanIcon size={14} />}
        data-cy="action_plan_btn"
      >
        Action Plan
      </StyledButton>
    ) : undefined;

  const extraActions = actionPlanButton ? (
    <ButtonGroup>{actionPlanButton}</ButtonGroup>
  ) : undefined;

  return (
    <>
      <PageWrapper
        title={t('TITLE')}
        isLoading={isLoading}
        error={error as TRtkErrorType}
        emptyWrapper={{
          isEmpty,
          alt: 'chess queen',
          icon: EIconNames.CHESS_QUEEN,
          emptyText: t('empty-view.EMPTY_TEXT'),
        }}
        actions={extraActions}
      >
        <AllELCsTabs
          defaultActiveKey={tabInUrl}
          onChange={onTabChange}
          items={tabItems}
        />
      </PageWrapper>
      {elcLightboxData && showLightbox && (
        <ELCLightboxModal
          open={showLightbox}
          onClose={handleCloseLightbox}
          lightboxData={elcLightboxData}
        />
      )}
    </>
  );
};

export default AllELCs;
